/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnInit,
    Optional,
    Self,
    ViewChild, ViewEncapsulation
} from '@angular/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NgControl } from '@angular/forms';
import { NumberRange } from '../../../../types';
import { DropDownItem } from '../../../../modules/reports/state/dataForm.model';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
    selector: 'dash-gis-number-range',
    templateUrl: './number-range.component.html',
    styleUrls: ['./number-range.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class NumberRangeComponent implements OnInit, ControlValueAccessor {
    private static nextId = 0;

    range: FormGroup;

    @Input() options: DropDownItem[];
    @Input() label: string;
    @Input() minPlaceholder = 'From:';
    @Input() maxPlaceholder = 'To:';

    @Input()
    get value() {
        return {
            min: parseInt(this.range.value['min'], 10),
            max: parseInt(this.range.value['max'], 10),
        };
    }

    set value(value: NumberRange) {
        this.range.patchValue({
            min: value?.min,
            max: value?.max,
        });
    }
    @Input()
    get disabled(): boolean { return this.disabledInternal; }
    set disabled(value: boolean) {
        this.disabledInternal = coerceBooleanProperty(value);
        if (this.disabledInternal) {
            this.range.disable();
        } else {
            this.range.enable();
        }
    }
    private disabledInternal: boolean;
    private dispose$ = new Subject<void>();

    onChange = (_: any) => {};
    onTouched = () => {};

    constructor(
        fb: FormBuilder,
        @Optional() @Self() public ngControl: NgControl,
        @Optional() public parentFormField: MatFormField,
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef<HTMLElement>
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        this.range = fb.group({
            min: null,
            max: null,
        });
    }

    handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
        this.onChange(this.value);
    }

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.dispose$.next();
    }

    // region ControlValueAccessor
    writeValue(val: NumberRange): void {
        this.value = val;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
        this.range.valueChanges.pipe(takeUntil(this.dispose$)).subscribe((value) => this.onChange(this.value));
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
    // endregion

    compareValueFn(a, b): boolean {
        if (a === undefined || b === undefined) {
            return a === b;
        }
        return parseFloat(a) === parseFloat(b);
    }

    get shouldDisplaySelect() {
        return this.options.length > 0;
    }
}
